import {Grid2, Grid2 as Grid} from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {useModelState, ValidatorOptions} from "@/lib/modelstate";
import * as React from "react";
import {useMemo, useRef} from "react";
import {terminationInitiatorOptions} from "@/datacaches";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {CommonI18N, CompanyI18N, UserI18N} from "@/translations";
import {convertDate} from "@/lib/utils";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import RadioButtonInputField from "@/lib/components/inputfields/radio-button-field";
import {CompanyDTO, CompanyTerminationDTO} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {companyApi} from "@/boxhub-api";
import {useDialogController} from "@/lib/dialog-controller";
import {transformToErrorShape} from "@/lib/accessor";
import {DialogEx} from "@/lib/components/DialogEx";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";

export type TerminationCompanyDialogProps = {
    open: boolean;
    close: () => void;
    onSuccess?: () => void;
    company?: CompanyDTO;
};

type TerminationCompanyValidation = Omit<CompanyTerminationDTO, "requestExecDate"> & {
    requestExecDate: any;
} & Pick<CompanyTerminationDTO, "requestReason" | "requestSource">;

const useSchema = () => {
    const {t} = useTranslation();
    return useMemo(() => {
        const schema = yup.object().shape({
            requestExecDate: yup
                    .date()
                    .required(t(CompanyI18N.termination.validation.dateRequired))
                    .transform(convertDate)
                    .typeError(t(CompanyI18N.termination.validation.dateInvalid)),

            requestReason: yup
                    .string()
                    .required(t(CompanyI18N.termination.validation.reasonRequired))
                    .min(
                            Constants.REASON_LENGTH_MIN,
                            t(CompanyI18N.termination.validation.reasonMin, {min: Constants.REASON_LENGTH_MIN})
                    )
                    .max(
                            Constants.REASON_LENGTH_MAX,
                            t(CompanyI18N.termination.validation.reasonMax, {max: Constants.REASON_LENGTH_MAX})
                    ),

            requestSource: yup
                    .string()
                    .required(t(CompanyI18N.termination.validation.initiatorRequired))
                    .oneOf(terminationInitiatorOptions, t(CompanyI18N.termination.validation.initiatorInvalid))
        });
        const typed = schema as yup.ObjectSchema<TerminationCompanyValidation>;
        return typed as yup.ObjectSchema<CompanyTerminationDTO>;
    }, [t]);
};

export const TerminationCompanyDialog = ({open, close, onSuccess, company}: TerminationCompanyDialogProps) => {
    const {t} = useTranslation();
    const [DialogNode, showDialog] = useDialogController();
    const terminationInitiatorRadioButtonOptions = terminationInitiatorOptions.map((option) => ({
        label: option && t(CommonI18N.enum.terminationInitiator[option]),
        value: option
    }));
    const schema = useSchema();

    const errorRef = useRef({key: "", error: {}});

    const validator = async (model: CompanyTerminationDTO, options: ValidatorOptions<CompanyTerminationDTO>) => {
        if (errorRef.current.key !== model.requestExecDate) {
            errorRef.current.key = model.requestExecDate;
            const response = await companyApi.validateTerminateDates({
                requestExecDate: model.requestExecDate
            });
            errorRef.current.error = transformToErrorShape(response.data?.error?.errors);
        }
        return errorRef.current.error;
    };

    const {model, errors, onChange, save, validate} = useModelState<CompanyTerminationDTO, number>({
        initialState: {} as CompanyTerminationDTO,
        schema,
        id: company?.id,
        loader: companyApi.initiateCompanyTermination,
        updater: companyApi.terminateCompany,
        validator
    });

    const handleConfirmTermination = () => {
        save()?.then(() => {
            close();
            onSuccess?.();
        });
    };

    const salutationOfCEO = useMemo(() => {
        const salutation = company?.contactPerson?.salutation;
        if (salutation) {
            return t(CommonI18N.enum.salutation[salutation]);
        } else {
            return "";
        }
    }, [t, company]);

    const countryOfCompany = useMemo(() => {
        const country = company?.companyAddress?.country;
        if (country) {
            return t(CommonI18N.enum.country[country]);
        } else {
            return "";
        }
    }, [t, company]);

    const ceoFirstName = useMemo(() => company?.contactPerson?.firstName ?? "", [company]);

    const ceoLastName = useMemo(() => company?.contactPerson?.lastName ?? "", [company]);

    const handleTerminateClick = () => {
        if (validate()) {
            showDialog((open, close) => (
                    <ConfirmationDialog
                            open={open}
                            onClose={close}
                            onConfirm={handleConfirmTermination}
                            title={t(CompanyI18N.termination.dialog.confirmTitle, {companyName: company?.name})}
                            message={t(CompanyI18N.termination.dialog.confirmMessage, {companyName: company?.name})}
                            confirmButtonText={t(CommonI18N.message.yes)}
                            cancelButtonText={t(CommonI18N.message.no)}
                    />
            ));
        }
    };

    const buttons = (
            <>
                <Grid2>
                    <ButtonEx label={t(CommonI18N.action.cancel)} color="secondary" onClick={close}/>
                </Grid2>
                <Grid2>
                    <ButtonEx
                            label={t(CompanyI18N.termination.action.terminate)}
                            color="primary"
                            onClick={handleTerminateClick}
                    />
                </Grid2>
            </>
    );

    return (
            <>
                <DialogEx
                        title={t(CompanyI18N.termination.dialog.title, {companyName: company?.name})}
                        open={open}
                        onClose={close}
                        maxWidth="sm"
                        fullWidth
                        buttons={buttons}
                >
                    <Grid container spacing={2} padding={2} width="100%">
                        <Grid width="100%">
                            <StringInputField value={company?.name} label={t(CompanyI18N.form.field.name)}
                                              disabled/>
                        </Grid>
                        <Grid width="100%">
                            <StringInputField
                                    label={t(CompanyI18N.form.field.street)}
                                    value={[company?.companyAddress?.street, company?.companyAddress?.houseNumber]
                                            .filter(Boolean)
                                            .join(" ")}
                                    disabled
                            />
                        </Grid>
                        <Grid container spacing={2} width="100%">
                            <Grid size={{xs: 6}}>
                                <StringInputField
                                        label={t(CompanyI18N.form.field.postalCode)}
                                        value={company?.companyAddress?.zip}
                                        disabled
                                />
                            </Grid>
                            <Grid size={{xs: 6}}>
                                <StringInputField
                                        label={t(CompanyI18N.form.field.city)}
                                        value={company?.companyAddress?.city}
                                        disabled
                                />
                            </Grid>
                        </Grid>
                        <Grid width="100%">
                            <StringInputField
                                    label={t(CompanyI18N.form.field.country)}
                                    value={countryOfCompany}
                                    disabled
                            />
                        </Grid>
                        <Grid container spacing={2} width="100%">
                            <Grid size={{xs: 2}}>
                                <StringInputField
                                        label={t(UserI18N.form.field.salutation)}
                                        value={salutationOfCEO}
                                        disabled
                                />
                            </Grid>
                            <Grid size={{xs: 5}}>
                                <StringInputField label={t(UserI18N.form.field.firstName)} value={ceoFirstName}
                                                  disabled/>
                            </Grid>
                            <Grid size={{xs: 5}}>
                                <StringInputField label={t(UserI18N.form.field.lastName)} value={ceoLastName} disabled/>
                            </Grid>
                        </Grid>
                        <Grid width="100%">
                            <DateInputField
                                    name="requestExecDate"
                                    label={t(CompanyI18N.termination.field.date)}
                                    value={model?.requestExecDate}
                                    error={errors?.requestExecDate}
                                    onChange={onChange}
                            />
                        </Grid>
                        <Grid width="100%">
                            <StringInputField
                                    name="requestReason"
                                    label={t(CompanyI18N.termination.field.reason)}
                                    value={model?.requestReason}
                                    error={errors?.requestReason}
                                    onChange={onChange}
                                    maxChars={Constants.REASON_LENGTH_MAX}
                                    multiline
                            />
                        </Grid>
                        <Grid width="100%">
                            <RadioButtonInputField
                                    name="requestSource"
                                    options={terminationInitiatorRadioButtonOptions}
                                    label={t(CompanyI18N.termination.field.initiator)}
                                    value={model?.requestSource}
                                    error={errors?.requestSource}
                                    onChange={onChange}
                            />
                        </Grid>
                    </Grid>
                </DialogEx>
                <DialogNode/>
            </>
    );
};
